import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import ImageSlider from '../../../components/ImageSlider'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import ReactVideoPlayer from '../../../components/ReactPlayer/VideoPlayer'
import GalleryWidget from '../../../components/PhotoDistribution/GalleryWidget'
import { PHOTO_DIST_API_BASE, PHOTO_DIST_CF_ROOT, PHOTO_DIST_ORG } from '../../../config'
import { useTheme } from '@emotion/react'
import useMediaQuery from '@mui/material/useMediaQuery'
import Grid from '@mui/material/Grid'
import Container from '@mui/material/Container'

export default function Media ({ eventDetails }) {
  const { t } = useTranslation('race')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const imageRoot = 'https://d1hi0tbu2kom2c.cloudfront.net/img/races/l1smedia/'
  const images = Array.from({ length: 28 }, (_, i) => `${i + 1}.jpg`)

  const [open, setOpen] = useState(false)
  const [dialogType, setDialogType] = useState(null)
  const [password, setPassword] = useState('')
  const [accessGranted, setAccessGranted] = useState(false)
  const [passwordError, setPasswordError] = useState('')

  const [isMainVideoExpanded, setIsMainVideoExpanded] = useState(false)
  const [isSecondaryVideoExpanded, setIsSecondaryVideoExpanded] = useState(false)

  // Photo distribution (photo_distribution project) wiring. The same eventId/orgId pair
  // is used for both the runner gallery (BIB search) and the photographer upload page.
  // Default the eventId to the race slug so the BIB gallery works without extra setup;
  // `mediaEventId` overrides it when the race uses a different backend event id.
  const mediaEventId = eventDetails?.mediaEventId || eventDetails?.slug || ''
  const mediaOrgId = eventDetails?.mediaOrgId || PHOTO_DIST_ORG

  const handleClose = () => {
    setOpen(false)
    setPassword('')
    setAccessGranted(false)
    setPasswordError('')
    setDialogType(null)
  }

  const handlePasswordSubmit = () => {
    if (!password) return
    // The "password" is a plain string from the race document, not a secret.
    if (password.trim() !== String(eventDetails?.mediaPassword ?? '').trim()) {
      setPasswordError(t('media.invalidPassword'))
      return
    }
    setAccessGranted(true)
  }

  const handlePlay = videoId => {
    if (isMainVideoExpanded) { setIsMainVideoExpanded(false) }
    if (isSecondaryVideoExpanded) { setIsSecondaryVideoExpanded(false) }
    if (videoId === 1) {
      setIsMainVideoExpanded(true)
    } else if (videoId === 2) {
      setIsSecondaryVideoExpanded(true)
    }
  }

  const handleStop = () => {
    setIsMainVideoExpanded(false)
    setIsSecondaryVideoExpanded(false)
  }

  return (
    <Container maxWidth='xl'>
      {eventDetails.finalEventVideo && (
      <Box sx={{ mb: 8 }}>
        <Typography
          variant='h5' gutterBottom sx={{
          fontWeight: 'bold'
        }}
        >
          {t('media.raceVideos')}
        </Typography>
        <Grid container spacing={4}>
          {eventDetails.finalEventVideo?.[0] &&
          <Grid size={{ xs: 12, sm: 6 } }>
            <ReactVideoPlayer
              videoUrl={eventDetails.finalEventVideo?.[0]}
              isExpanded={isMainVideoExpanded}
              onClose={() => handleStop()}
              onPlay={() => handlePlay(1)}
              videoId={1}
            />
            <Typography
              variant='h6' sx={{
              fontWeight: 'bold'
            }}
            >{t('media.videoEpic')}</Typography>
          </Grid>}
          {eventDetails.finalEventVideo?.[1] &&
            <Grid size={{ xs: 12, sm: 6 } }>
              <ReactVideoPlayer
                videoUrl={eventDetails.finalEventVideo?.[1]}
                poster='/img/races/ls.png'
                isExpanded={isSecondaryVideoExpanded}
                onClose={() => handleStop()}
                onPlay={() => handlePlay(2)}
                videoId={2}
              />
              <Typography
                variant='h6' sx={{
                fontWeight: 'bold'
              }}
              >{t('media.videoDocumentary')}</Typography>
            </Grid>}
        </Grid>
      </Box>
      )}

      <Box sx={{ minHeight: 420, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' } }}>
        <Box sx={{ maxWidth: 360 }}>
          <Typography
            variant='h5' gutterBottom sx={{
            fontWeight: 'bold'
          }}
          >
            {t('media.racePhotos')}
          </Typography>

          {/* <Typography gutterBottom fontWeight='bold'>
          <span style={{ display: 'inline-block' }}>
            Processing&nbsp;
            <span className='dot'>.</span>
            <span className='dot'>.</span>
            <span className='dot'>.</span>
          </span>
          <style>{`
            @keyframes blink {
              0% { opacity: 0; }
              50% { opacity: 1; }
              100% { opacity: 0; }
            }
            .dot {
              display: inline-block;
              animation: blink 1.2s infinite;
            }
            .dot:nth-child(2) { animation-delay: 0.2s; }
            .dot:nth-child(3) { animation-delay: 0.4s; }
          `}</style>
        </Typography> */}
          {eventDetails.showPasswordForMedia
          ? <>
            <Typography
              sx={{
              my: 4
            }}
            >
              {t('media.photosWithPassword')}
            </Typography>
            <Button variant='contained' sx={{ mb: 4 }} onClick={() => { setDialogType('photos'); setOpen(true) }}>{t('media.viewPhotos')}</Button>
          </>
          : <>
            <Typography
              sx={{
              my: 4
            }}
            >
              {t('media.photosNoPassword')}
            </Typography>
            <Button variant='contained' sx={{ mb: 4 }} onClick={() => { setDialogType('photos'); setOpen(true) }}>{t('media.viewPhotos')}</Button>
          </>
        }

          {eventDetails.showFeedbackForm !== false && eventDetails.linkToFeedbackFrom && (
          <>
            <Divider sx={{ my: 2 }} />

            <Typography
              variant='h5' gutterBottom sx={{
              fontWeight: 'bold'
            }}
            >
              {t('media.feedback')}
            </Typography>
            <Typography
              sx={{
              my: 4
            }}
            >
              {t('media.feedbackIntro')}
            </Typography>
            <Button variant='contained' sx={{ mb: 4 }} onClick={() => { setDialogType('feedback'); setOpen(true) }}>{t('media.feedbackForm')}</Button>
          </>
          )}

        </Box>
        {eventDetails.showImageSliderInMedia &&
          <Box sx={{ flex: 1 }}>
            <ImageSlider {...{ images, imageRoot }}/>
          </Box>}
        <Dialog open={open} onClose={handleClose} maxWidth={dialogType === 'feedback' ? false : (dialogType === 'photos' && accessGranted ? 'lg' : 'sm')} fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {dialogType === 'photos' ? (accessGranted ? t('mediaGallery.find_your_photos') : t('media.enterPassword')) : t('media.feedbackForm')}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ my: { xs: 2, md: 6 }, px: { xs: 2, md: 3 } }}>
            {dialogType === 'feedback' && isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src='/peaks-logo-white.png' alt={t('media.logoAlt')} style={{ height: 32 }} />
            </Box>
            )}
            {dialogType === 'photos'
              ? (
                  accessGranted
                    ? <>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <img src='/peaks-logo-white.png' alt={t('media.logoAlt')} style={{ height: 32 }} />
                      </Box>
                      <GalleryWidget
                        orgId={mediaOrgId}
                        eventId={mediaEventId}
                        apiBaseUrl={PHOTO_DIST_API_BASE}
                        cfRoot={PHOTO_DIST_CF_ROOT}
                        resultsHref={eventDetails?.slug ? `/races/${eventDetails.slug}/results` : ''}
                      />
                    </>
                    : <>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                          <img src='/peaks-logo-white.png' alt={t('media.logoAlt')} style={{ height: 32 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2, p: 3 }}>
                          <TextField
                            color='icon'
                            label={t('media.password')}
                            type='password'
                            value={password}
                            sx={{ background: '#222' }}
                            onChange={(e) => { setPassword(e.target.value); setPasswordError('') }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handlePasswordSubmit() }}
                          />
                          <Button
                            variant='contained'
                            disabled={!password}
                            onClick={handlePasswordSubmit}
                          >
                            {t('media.go')}
                          </Button>
                        </Box>
                        {passwordError && (
                          <Typography color='error' align='center'>{passwordError}</Typography>
                        )}
                      </Box>
                    </>
                )
              : (
                <>
                  <iframe title={t('media.feedbackFormTitle')} src={`${eventDetails?.linkToFeedbackFrom}/viewform?embedded=true`} width='100%' height='2682' frameBorder='0' marginHeight='0' marginWidth='0'>{t('media.loading')}</iframe>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button onClick={handleClose}>{t('media.close')}</Button>
                  </Box>
                </>
                )
          }
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  )
}
